import React, { useState, useRef, useEffect } from "react";
import { styled } from "@mui/material/styles";

// Reusable styled component (you can also pass styles as props)
const PlaceholderContainer = styled("div")(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  left: theme.spacing(3),
  right: theme.spacing(3),
  color: "rgba(23, 23, 23, 0.3)",
  opacity: 0.8,
  pointerEvents: "none",
  transition: "opacity 0.5s ease, transform 0.5s ease",
  fontSize: "15px",
  lineHeight: "18px",
  letterSpacing: "2px",
  fontWeight: 400,
}));

/**
 * AnimatedPlaceholder component
 *
 * Props:
 * - placeholders: array of objects { text: string, language?: string }
 * - interval (optional): time in ms between rotations (default 3000)
 */
const AnimatedPlaceholder = ({
  placeholders,
  interval = 3000,
  showPlaceholder,
  onChangeLanguage,
}) => {
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const [animationState, setAnimationState] = useState({
    running: true,
    transitioning: false,
    direction: "out", // 'out' means fading out, 'in' means fading in
  });

  useEffect(() => {
    onChangeLanguage?.(placeholders[currentPlaceholderIndex].language);
  }, [currentPlaceholderIndex, onChangeLanguage, placeholders]);

  const animationTimerRef = useRef(null);
  const transitionTimerRef = useRef(null);
  const isFirstAnimation = useRef(true);

  const getPlaceholderStyle = () => {
    const style = {};
    if (animationState.transitioning) {
      style.transform =
        animationState.direction === "out"
          ? "translateY(10px)"
          : "translateY(0)";
      // When fading out, opacity is 0; when fading in, opacity returns to default (0.8)
      style.opacity = animationState.direction === "out" ? 0 : 0.8;
    } else {
      // When not transitioning, show default opacity (0.8) if placeholder should be shown, else 0
      style.opacity = showPlaceholder ? 0.8 : 0;
    }
    return style;
  };

  const rotatePlaceholders = () => {
    if (!showPlaceholder) {
      // If placeholder shouldn't be shown, cancel any further animations.
      return;
    }

    if (animationState.running) {
      // Start fade-out transition
      setAnimationState((prev) => ({
        ...prev,
        transitioning: true,
        direction: "out",
      }));

      // After fade-out, change text and start fade-in
      transitionTimerRef.current = setTimeout(() => {
        setCurrentPlaceholderIndex(
          (prevIndex) => (prevIndex + 1) % placeholders.length
        );
        setAnimationState((prev) => ({ ...prev, direction: "in" }));

        setTimeout(() => {
          setAnimationState((prev) => ({ ...prev, transitioning: false }));
        }, 500);
      }, 500);

      // Schedule the next rotation
      animationTimerRef.current = setTimeout(rotatePlaceholders, interval);
    }
  };

  useEffect(() => {
    if (showPlaceholder) {
      const timer = setTimeout(
        () => {
          isFirstAnimation.current = false;
          rotatePlaceholders();
        },
        isFirstAnimation.current ? interval - 1000 : interval
      );
      return () => {
        clearTimeout(timer);
        if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
        if (transitionTimerRef.current)
          clearTimeout(transitionTimerRef.current);
      };
    }
  }, [showPlaceholder, animationState.running, interval]);

  return (
    <PlaceholderContainer style={getPlaceholderStyle()}>
      {placeholders[currentPlaceholderIndex].text}
    </PlaceholderContainer>
  );
};

export default AnimatedPlaceholder;
